import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
// import { MutateOptions } from 'react-query';
import { Card, Flex } from 'antd';
import { tr } from 'date-fns/locale';
import { equals } from 'ramda';
import { ActionsXorCropArea, RotationDirection, SideUpdate, SideWithAsset } from 'shared-types';
import styled from 'styled-components';

import Box from 'components/Box/Box';
import { Area } from 'components/EasyCrop/types';
import FilePreview from 'components/ProductSide/FilePreview';
import Toolbox from 'components/ProductSide/Toolbox';

import useAssetsStore from 'hooks/stores/useAssetsStore';
import useSessionStore from 'hooks/stores/useSessionStore';
import useActions from 'hooks/useActions';
import useCrop from 'hooks/useCrop';
import useDownloadPDF from 'hooks/useDownloadPDF';
import useEditor from 'hooks/useEditor';

import API from 'services/API';

import {
  calculateCropToIncludeBleed,
  createCropArea,
  flipTrimbox,
  getSizeInPixel,
  rotateTrimbox,
} from 'utils/pdf';

import CropModal from './CropModal';

// Set to true to enable detailed console debugging for scaling issues
const DEBUG_SCALING = true;

// Interface voor drag & drop event handlers
interface DragDropHandlers {
  onDragEnter?: (e: React.DragEvent<HTMLElement>) => void;
  onDragOver?: (e: React.DragEvent<HTMLElement>) => void;
  onDragLeave?: (e: React.DragEvent<HTMLElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLElement>) => void;
}

export const SCard = styled(Card)`
  width: 100%;
  border-radius: 8px;
  margin-bottom: 1rem;
  padding: 1rem;
  height: 100%;
`;

export const SDelete = styled(Box)`
  display: flex;
  justify-content: center;
`;

type Props = {
  side: SideWithAsset;
  onPdfPreviewGenerated: (previewUrl: string) => void;
} & DragDropHandlers;

const PageCardEditor = ({
  side,
  onPdfPreviewGenerated,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
}: Props) => {
  // Get asset from store
  const asset = useAssetsStore((state) => state.byId[side?.assetId]);

  const isLoadingRef = useRef(true);
  const historyRef = useRef<(ActionsXorCropArea & { type: 'actions' | 'cropArea' })[]>([]); // Added undo history
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const { bleed, productSize } = useSessionStore((state) => ({
    bleed: state.session?.bleed,
    productSize: state.session?.productSize,
  }));
  const [pdfPreview, setPdfPreview] = useState<string | null>(null);
  const sessionId = useSessionStore((state) => state.sessionId);
  const queryClient = useQueryClient();
  const [isOpened, setIsOpened] = useState(false);

  const isRotatingRef = useRef(false);
  const isHandlingRotation = useRef(false);
  const isUserActionRef = useRef(false);

  // State to track PDF load status and optimal scale
  const [isPdfLoaded, setIsPdfLoaded] = useState(false);
  const [optimalScale, setOptimalScale] = useState(0);

  const { crop, zoom, onCropChange, onZoomChange, onCropComplete, croppedArea, resetZoom } =
    useCrop();
  const [capturedArea, setCapturedArea] = useState<Area | null>(null);

  const { actions, id: sideId, productId, cropArea } = side || {};
  // const { pdfFile: originalPdf, isDownloading } = useDownloadPDF({ fileUrl: asset.previewFile });
  const { newActions, setActions, handleFlip: flipAction } = useActions(actions);

  const updateAssetLoadingStatus = useAssetsStore((state) => state.updateAssetLoadingStatus);

  const { mutate } = useMutation(
    `updateSide-${sideId}`,
    (actionsOrCropArea: SideUpdate) => {
      console.log('🌐 API.updateSide: Called with:', actionsOrCropArea);
      console.log('🌐 API.updateSide: Current actions.flip =', actions.flip);
      return API.updateSide(sessionId, sideId, actionsOrCropArea);
    },
    {
      onMutate: (variables) => {
        console.log('🔄 Mutation onMutate: variables =', variables);
        console.log('🔄 Mutation onMutate: actions.flip =', actions.flip);
        updateAssetLoadingStatus(side?.assetId, 'loading');
      },
      onSuccess: (data) => {
        console.log('✅ Mutation onSuccess: data =', data);
        console.log('✅ Mutation onSuccess: data.actions =', data?.actions);
        queryClient.invalidateQueries('products');
        // Don't set success here - wait for preview to be available
      },
      onError: (error) => {
        console.log('❌ Mutation onError: error =', error);
        updateAssetLoadingStatus(side?.assetId, 'error');
      },
    },
  );

  const aspectRatio = Number(productSize?.width) / Number(productSize?.height);
  let assetHasTrimbox = false;
  if (asset?.dimensions && asset?.metadata?.trimbox) {
    if (
      Math.abs(asset.dimensions.width - asset.metadata.trimbox.width) > 0.01 ||
      Math.abs(asset.dimensions.height - asset.metadata.trimbox.height) > 0.01 ||
      asset.metadata.trimbox.x > 0 ||
      asset.metadata.trimbox.y > 0
    ) {
      assetHasTrimbox = true;
    }
  }

  const disableCrop = false;
  //newActions.flip || newActions.rotation !== 0 || actions.flip || actions.rotation !== 0;

  // Function to add actions or cropArea to the undo history
  const addToHistory = useCallback(
    (type: 'actions' | 'cropArea') => {
      if (type === 'actions') {
        historyRef.current.push({ type: 'actions', actions });
      } else if (type === 'cropArea') {
        historyRef.current.push({ type: 'cropArea', cropArea: capturedArea });
      }
    },
    [actions, capturedArea],
  );

  const handleFlip = useCallback(() => {
    isUserActionRef.current = true;

    addToHistory('actions'); // Save current state before mutating

    const updatedActions = {
      ...actions,
      flip: !actions.flip, // Toggle flip state
    };

    mutate(
      { actions: updatedActions },
      {
        onError: () => {
          setActions(actions); // Rollback in case of error
        },
        onSuccess: () => {
          isRotatingRef.current = false;
        },
        onSettled: () => {
          isRotatingRef.current = false;
        },
      },
    );
  }, [actions, mutate, addToHistory]);

  const handleUndo = useCallback(() => {
    if (historyRef.current.length === 0) return;

    const lastState = historyRef.current.pop();
    if (lastState?.type === 'actions') {
      //  setActions(lastState.actions!); // Revert actions
      mutate({ actions: lastState.actions! });
    } else if (lastState?.type === 'cropArea') {
      setCapturedArea(lastState.cropArea!); // Revert cropArea
      mutate({ cropArea: lastState.cropArea! });
    }
  }, [mutate, setActions]);

  const handleRotate = useCallback(
    (direction: RotationDirection) => {
      if (isRotatingRef.current) return;
      isRotatingRef.current = true;

      addToHistory('actions'); // Save before mutation

      const newRotation = Math.floor(
        (((actions.rotation + (direction === 'right' ? 90 : -90)) % 360) + 360) % 360,
      );

      const updatedActions = {
        ...actions,
        rotation: newRotation,
      };

      // Step 1: Update local state
      setActions(updatedActions);

      // Step 2: Directly mutate with updated actions
      mutate(
        { actions: updatedActions },
        {
          onError: () => {
            setActions(actions); // Rollback on error
          },
          onSettled: () => {
            isRotatingRef.current = false;
          },
        },
      );
    },
    [actions, mutate, setActions, addToHistory],
  );

  // const handleRotate = useCallback(
  //   (direction: RotationDirection) => {
  //     if (isRotatingRef.current) return;
  //     isRotatingRef.current = true;

  //     const newRotation = Math.floor(
  //       (((actions.rotation + (direction === 'right' ? 90 : -90)) % 360) + 360) % 360,
  //     );

  //     const updatedActions = {
  //       ...actions,
  //       rotation: newRotation,
  //     };

  //     // Optimistische update
  //     //setActions(updatedActions);

  //     mutate(
  //       { actions: updatedActions },
  //       {
  //         onError: () => {
  //           // Rollback bij error
  //           setActions(actions);
  //         },
  //         onSuccess: () => {
  //           isRotatingRef.current = false;
  //         },
  //         onSettled: () => {
  //           isRotatingRef.current = false;
  //         }
  //       }
  //     );
  //   },
  //   [actions, mutate, setActions]
  // );
  // const generatedPdf = useEditor({
  //   pdfFile: originalPdf,
  //   newActions,
  //   metaData: asset.metadata,
  //   dimensions: asset.dimensions,
  //   cropArea: cropArea || capturedArea,
  //   asset,
  // });

  const handleZoomOut = () => {
    if (!bleed || !productSize) return;
    isUserActionRef.current = true;

    addToHistory('cropArea'); // Save before mutation

    let assetTrimbox = asset.dimensions.trimbox;
    let assetPage = {
      width: asset.dimensions.width,
      height: asset.dimensions.height,
    };

    // Apply rotation to trimbox if asset is rotated
    if (actions.rotation && actions.rotation !== 0) {
      assetTrimbox = rotateTrimbox({
        trimbox: assetTrimbox,
        page: assetPage,
        rotation: actions.rotation,
      });

      // Update page dimensions after rotation
      if (actions.rotation === 90 || actions.rotation === 270) {
        assetPage = {
          width: asset.dimensions.height,
          height: asset.dimensions.width,
        };
      }

      console.log('handleZoomOut: Applied rotation', actions.rotation, 'to trimbox:', assetTrimbox);
    }

    // Apply flip transformation if needed
    if (actions.flip) {
      assetTrimbox = flipTrimbox({ trimbox: assetTrimbox, page: assetPage });
      console.log('handleZoomOut: Applied flip to trimbox:', assetTrimbox);
    }

    console.log('Zoom OUT');
    console.log('trimbox', assetTrimbox);
    const productTrimbox = {
      x: 0,
      y: 0,
      width: getSizeInPixel(Number(productSize.width) + Number(bleed.left) + Number(bleed.right)),
      height: getSizeInPixel(Number(productSize.height) + Number(bleed.top) + Number(bleed.bottom)),
    };
    console.log('productTrimbox', productTrimbox);
    console.log('productSize', productSize);
    const crop = calculateCropToIncludeBleed(assetTrimbox, productSize, bleed);
    console.log('calculated crop', crop);

    setCapturedArea(crop);
  };

  const handleSetOriginal = () => {
    if (!bleed || !productSize) return;

    addToHistory('actions'); // Save before mutation

    // Step 1: Reset actions state (no mutate)
    setActions({
      rotation: 0,
      flip: false,
      type: 'CROP',
      place: 'GROSS',
    });

    // Step 2: Calculate and set crop area with original trimbox
    const assetTrimbox = asset.dimensions.trimbox;
    const productTrimbox = {
      x: getSizeInPixel(Number(bleed.left || 0)),
      y: getSizeInPixel(Number(bleed.top || 0)),
      width: getSizeInPixel(Number(productSize.width)),
      height: getSizeInPixel(Number(productSize.height)),
    };
    const crop = createCropArea(assetTrimbox, productTrimbox);

    resetZoom();

    // Step 3: Directly mutate with reset actions and new crop area
    const resetActions = {
      rotation: 0,
      flip: false,
      type: 'CROP',
      place: 'GROSS',
    };

    mutate({
      actions: resetActions,
      cropArea: crop,
    });
  };

  const handleSetToFit = () => {
    if (!bleed || !productSize) return;

    console.log('🚀 handleSetToFit: START - actions.flip =', actions.flip);
    console.log('🚀 handleSetToFit: Full actions =', actions);

    isUserActionRef.current = true;

    addToHistory('cropArea'); // Save before mutation

    let assetTrimbox = asset.dimensions.trimbox;
    let assetPage = {
      width: asset.dimensions.width,
      height: asset.dimensions.height,
    };

    // Apply rotation to trimbox if asset is rotated
    if (actions.rotation && actions.rotation !== 0) {
      assetTrimbox = rotateTrimbox({
        trimbox: assetTrimbox,
        page: assetPage,
        rotation: actions.rotation,
      });

      // Update page dimensions after rotation
      if (actions.rotation === 90 || actions.rotation === 270) {
        assetPage = {
          width: asset.dimensions.height,
          height: asset.dimensions.width,
        };
      }

      console.log(
        'handleSetToFit: Applied rotation',
        actions.rotation,
        'to trimbox:',
        assetTrimbox,
      );
    }

    const productTrimbox = {
      x: getSizeInPixel(Number(bleed.left || 0)),
      y: getSizeInPixel(Number(bleed.top || 0)),
      width: getSizeInPixel(Number(productSize.width)),
      height: getSizeInPixel(Number(productSize.height)),
    };

    const crop = createCropArea(assetTrimbox, productTrimbox, true);
    console.log('handleSetToFit: Final crop area:', crop);
    console.log('🚀 handleSetToFit: END - actions.flip still =', actions.flip);

    resetZoom(0.5);
    // onCropChange({
    //   x: crop.x,
    //   y: crop.y
    // });
    console.log('🔧 handleSetToFit: Calling setCapturedArea with crop:', crop);
    console.log('🔧 handleSetToFit: actions.flip before setCapturedArea =', actions.flip);
    setCapturedArea(crop);
  };

  useEffect(() => {
    isUserActionRef.current = false;
    setCapturedArea(cropArea);
  }, [sideId, cropArea]);

  useEffect(() => {
    if (!isUserActionRef.current) return;

    if (capturedArea && !equals(capturedArea, cropArea) && !isHandlingRotation.current) {
      console.log('📡 MUTATION useEffect: actions.flip before mutate =', actions.flip);
      console.log('📡 MUTATION useEffect: Full actions before mutate =', actions);

      // Send both actions AND cropArea to prevent backend from resetting actions
      const payload = {
        actions: actions,
        cropArea: capturedArea,
      };
      console.log('📡 MUTATION useEffect: Sending to API:', payload);
      mutate(payload);
    }
  }, [capturedArea, cropArea, mutate, sideId, actions]);

  useEffect(() => {
    setActions(newActions);
  }, [newActions]);

  // Reset PDF load state and optimal scale when side changes
  useEffect(() => {
    DEBUG_SCALING && console.log('Side changed, resetting PDF load state');
    setIsPdfLoaded(false);
    setOptimalScale(0); // Reset state value

    // Dynamische fallback: bereken op basis van viewport, geen statische waarde
    if (visibleWrapperRef.current) {
      const viewport = {
        width: visibleWrapperRef.current.clientWidth,
        height: visibleWrapperRef.current.clientHeight,
      };

      // Default PDF size A4 (of maak een schatting op basis van productSize indien beschikbaar)
      const pdfSize = productSize
        ? {
            width: getSizeInPixel(Number(productSize.width)) * 2,
            height: getSizeInPixel(Number(productSize.height)) * 2,
          }
        : { width: 795, height: 1123 }; // ~A4

      const dynamicScale = Math.min(
        (viewport.width * 0.9) / pdfSize.width,
        (viewport.height * 0.9) / pdfSize.height,
      );

      // Stel een redelijke beperking in
      const boundedScale = Math.max(0.3, Math.min(dynamicScale, 0.9));
      latestScaleRef.current = boundedScale;

      DEBUG_SCALING && console.log('Dynamisch berekende fallback-schaal:', boundedScale);
    } else {
      // Absolute fallback als er geen container is
      latestScaleRef.current = 0.5;
    }

    DEBUG_SCALING && console.log('Reset latestScaleRef to:', latestScaleRef.current);
  }, [side.id, productSize]);

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth);

      const resizeObserver = new ResizeObserver((entries) => {
        if (entries[0]) {
          setContainerWidth(entries[0].target.clientWidth);
        }
      });

      resizeObserver.observe(containerRef.current);

      return () => {
        if (containerRef.current) {
          resizeObserver.unobserve(containerRef.current);
        }
      };
    }
  }, []);

  // Use ref to keep track of the latest scale value without waiting for state updates
  const latestScaleRef = useRef(0);
  const visibleWrapperRef = useRef<HTMLDivElement | null>(null);

  // Function to calculate optimal scale based on canvas and container dimensions
  const calculateOptimalScale = (
    canvasWidth: number,
    canvasHeight: number,
    containerWidth: number,
    containerHeight: number,
  ) => {
    if (!containerWidth || !containerHeight || !canvasWidth || !canvasHeight) {
      DEBUG_SCALING && console.log('Missing dimensions for scale calculation, using default');
      return 0.6; // Lower default fallback
    }

    // Calculate scale factors - how much we would need to scale the canvas to fit container
    // Add a 10% margin to ensure content fits with some padding
    const scaleX = (containerWidth * 0.9) / canvasWidth;
    const scaleY = (containerHeight * 0.9) / canvasHeight;

    DEBUG_SCALING &&
      console.log(
        `Scale calculation: container (${containerWidth}x${containerHeight}), ` +
          `canvas (${canvasWidth}x${canvasHeight}), scales: X=${scaleX.toFixed(3)}, Y=${scaleY.toFixed(3)}`,
      );

    // Use the smaller scale factor to ensure entire PDF fits in view
    const optimalScale = Math.min(scaleX, scaleY);

    // Ensure scale is within reasonable bounds
    // Lowered upper bound to avoid too large initial view
    const boundedScale = Math.max(0.3, Math.min(optimalScale, 0.95));

    DEBUG_SCALING &&
      console.log(`Final scale: ${boundedScale.toFixed(3)} (raw: ${optimalScale.toFixed(3)})`);

    return boundedScale;
  };

  const processGeneratedImage = (
    imageUrl: string,
    calculatedScale?: number,
    canvasDimensions?: { width: number; height: number },
  ) => {
    //    Als PDF al geladen is, gebruik bestaande schaal en geef afbeelding door
    if (!isPdfLoaded && latestScaleRef.current > 0) {
      // eerste keer overslaan, alleen isPdfLoaded zetten, zodat de tweede keer de schaal wordt gebruikt
      DEBUG_SCALING &&
        console.log('PDF already loaded, using existing scale', latestScaleRef.current);
      // Markeer PDF als geladen wanneer we een geldige schaal hebben
      setIsPdfLoaded(true);
      onPdfPreviewGenerated(imageUrl);
      return;
    }

    // Gebruik altijd DOM voor werkelijke afmetingen indien beschikbaar
    const canvasElements = document.getElementsByTagName('canvas');
    const canvas = canvasElements.length > 0 ? canvasElements[canvasElements.length - 1] : null;

    if (canvas) {
      DEBUG_SCALING &&
        console.log(
          'Werkelijke canvas afmetingen in processGeneratedImage:',
          canvas.width,
          'x',
          canvas.height,
        );
    }

    DEBUG_SCALING && console.log('Canvas elementen:', canvas, visibleWrapperRef.current);
    // Gebruik container afmetingen uit DOM
    if (canvas) {
      let containerWidth, containerHeight;

      // Optie 1: Gebruik visibleWrapperRef als het beschikbaar is
      if (visibleWrapperRef.current) {
        containerWidth = visibleWrapperRef.current.clientWidth;
        containerHeight = visibleWrapperRef.current.clientHeight;
        DEBUG_SCALING &&
          console.log('Gebruik visibleWrapperRef container:', containerWidth, 'x', containerHeight);
      }
      // Optie 2: Vind de TransformComponent container als visibleWrapperRef niet beschikbaar is
      else {
        // Zoek naar de transform component in de DOM
        const transformComponent = document.querySelector('.react-transform-component');
        if (transformComponent) {
          containerWidth = transformComponent.clientWidth;
          containerHeight = transformComponent.clientHeight;
          DEBUG_SCALING &&
            console.log(
              'Gebruik TransformComponent container:',
              containerWidth,
              'x',
              containerHeight,
            );
        }
        // Optie 3: Zoek de ouder container van het canvas
        else {
          let canvasParent = canvas.parentElement;
          while (canvasParent && canvasParent.clientWidth < 10) {
            canvasParent = canvasParent.parentElement;
          }

          if (canvasParent && canvasParent.clientWidth > 0) {
            containerWidth = canvasParent.clientWidth;
            containerHeight = canvasParent.clientHeight;
            DEBUG_SCALING &&
              console.log('Gebruik canvas parent container:', containerWidth, 'x', containerHeight);
          }
          // Optie 4: Fallback naar venster afmetingen
          else {
            containerWidth = window.innerWidth * 0.8; // 80% van vensterbreedte
            containerHeight = window.innerHeight * 0.7; // 70% van vensterhoogte
            DEBUG_SCALING &&
              console.log('Fallback naar vensterafmetingen:', containerWidth, 'x', containerHeight);
          }
        }
      }

      if (containerWidth && containerHeight) {
        DEBUG_SCALING && console.log('Container afmetingen:', containerWidth, 'x', containerHeight);
        DEBUG_SCALING && console.log('Canvas afmetingen:', canvas.width, 'x', canvas.height);

        // Bereken schaalfactoren op basis van werkelijke canvas en container afmetingen
        const scaleX = containerWidth / canvas.width;
        const scaleY = containerHeight / canvas.height;

        DEBUG_SCALING &&
          console.log('Berekende schaalfactoren:', scaleX.toFixed(3), 'x', scaleY.toFixed(3));

        // Gebruik kleinste schaal voor volledige weergave
        const optimalScale = Math.min(scaleX, scaleY) * 0.9; // 10% marge

        // Begrens de schaal voor optimale weergave
        const boundedScale = Math.max(0.1, Math.min(optimalScale, 0.9));

        DEBUG_SCALING &&
          console.log(
            'Berekende schaal:',
            boundedScale,
            'scaleX:',
            scaleX.toFixed(3),
            'scaleY:',
            scaleY.toFixed(3),
          );

        // Sla schaal op voor directe toegang en state updates
        latestScaleRef.current = boundedScale;
        setOptimalScale(boundedScale);

        // Markeer PDF als geladen wanneer we een geldige schaal hebben
        setIsPdfLoaded(true);
      }
    }
    // Fallback voor DOM canvas naar opgegeven dimensies
    else if (canvasDimensions?.width && canvasDimensions.height && visibleWrapperRef.current) {
      const containerWidth = visibleWrapperRef.current.clientWidth;
      const containerHeight = visibleWrapperRef.current.clientHeight;

      DEBUG_SCALING &&
        console.log(
          'Fallback: Container en canvas dimensies:',
          containerWidth,
          'x',
          containerHeight,
          'canvas:',
          canvasDimensions.width,
          'x',
          canvasDimensions.height,
        );

      const scaleX = containerWidth / canvasDimensions.width;
      const scaleY = containerHeight / canvasDimensions.height;
      const optimalScale = Math.min(scaleX, scaleY) * 0.9;
      const boundedScale = Math.max(0.1, Math.min(optimalScale, 0.9));

      DEBUG_SCALING && console.log('Fallback berekende schaal:', boundedScale);

      latestScaleRef.current = boundedScale;
      setOptimalScale(boundedScale);
      setIsPdfLoaded(true);
    }
    // Fallback naar calculatedScale indien opgegeven
    else if (calculatedScale && calculatedScale > 0.001) {
      const boundedScale = Math.max(0.1, Math.min(calculatedScale, 0.9));
      DEBUG_SCALING && console.log('Using calculatedScale:', boundedScale);

      latestScaleRef.current = boundedScale;
      setOptimalScale(boundedScale);
      setIsPdfLoaded(true);
    }

    // Geef de afbeelding altijd door aan de parent component
    onPdfPreviewGenerated(imageUrl);
  };
  const handleOpenModal = () => {
    setIsOpened(true);
    resetZoom();
    setCapturedArea(null);
  };

  const handleCloseModal = () => {
    setIsOpened(false);
    setCapturedArea(null);
  };

  const normalizeCropArea = (cropArea: any) => {
    return {
      x: Number(Number(cropArea?.x || 0).toFixed(10)),
      y: Number(Number(100 - cropArea?.height - cropArea?.y || 0).toFixed(10)),
      width: Number(Number(cropArea?.width || 100).toFixed(10)),
      height: Number(Number(cropArea?.height || 100).toFixed(10)),
    };
  };

  // on model close set captured area to cropped area
  const handleSaveAndCloseModal = () => {
    setIsOpened(false);
    isUserActionRef.current = true;
    // normalize y value to use left bottom as 0
    const area = normalizeCropArea(croppedArea);
    setCapturedArea(area || null);
  };

  return !asset ? (
    <div>Loading asset data...</div>
  ) : (
    <Card
      key={side.id}
      style={{ padding: 0, backgroundColor: '#f0f2f5' }}
      onDragEnter={(e) => {
        // Voorkom dat de Card de drag events stopt
        e.preventDefault();
        e.stopPropagation();
        onDragEnter && onDragEnter(e);
      }}
      onDragOver={(e) => {
        // Voorkom dat de Card de drag events stopt
        e.preventDefault();
        e.stopPropagation();
        onDragOver && onDragOver(e);
      }}
      onDragLeave={(e) => {
        // NOOIT dragLeave events volledig propageren binnen de editor
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={(e) => {
        // Zorg ervoor dat de drop events worden doorgegeven
        e.preventDefault();
        e.stopPropagation();
        onDrop && onDrop(e);
      }}
    >
      <Flex
        key={side.id}
        justify="flex-start"
        align="center"
        style={{ width: '100%', marginBottom: '0.5rem', height: '80vh', overflow: 'hidden' }}
        vertical
        onDragEnter={(e) => {
          // Voorkom dat de Flex container de drag events stopt
          e.preventDefault();
          e.stopPropagation();
          onDragEnter && onDragEnter(e);
        }}
        onDragOver={(e) => {
          // Voorkom dat de Flex container de drag events stopt
          e.preventDefault();
          e.stopPropagation();
          onDragOver && onDragOver(e);
        }}
        onDragLeave={(e) => {
          // NOOIT dragLeave events volledig propageren binnen de editor
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          // Zorg ervoor dat de drop events worden doorgegeven
          e.preventDefault();
          e.stopPropagation();
          onDrop && onDrop(e);
        }}
      >
        <Toolbox
          onRotate={(direction) => handleRotate(direction)}
          onFlip={handleFlip} // Updated to use the fixed function
          onZoomOut={handleZoomOut}
          onSetOriginal={handleSetOriginal}
          onSetToFit={handleSetToFit}
          onCrop={handleOpenModal}
          onUndo={handleUndo} // Undo action added
          productId={productId}
          sideId={sideId}
          showActions={['rotate', 'flip', 'crop', 'zoomout', 'reset', 'undo', 'set-to-fit']}
          disableActions={[
            ...(disableCrop ? ['crop' as const] : []),
            ...(assetHasTrimbox ? ['set-to-fit' as const] : []),
          ]} // Disable crop logic added
        />

        <PreviewImageAutoFit src={side?.previewImageUrl || ''} />
      </Flex>
      {isOpened && (
        <CropModal
          isOpen={isOpened}
          onClose={handleCloseModal}
          onSave={handleSaveAndCloseModal}
          sessionId={sessionId}
          sideId={sideId}
          crop={crop}
          zoom={zoom}
          aspectRatio={aspectRatio}
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onCropComplete={onCropComplete}
        />
      )}
    </Card>
  );

  isLoadingRef.current = false;
};

export const PreviewImageAutoFit = ({ src }: { src: string }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const transformWrapperRef = useRef<ReactZoomPanPinchRef | null>(null);
  const [initialScale, setInitialScale] = useState<number | null>(null);
  const [imgSize, setImgSize] = useState({ width: 1, height: 1 });
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  // 1. Laad de afbeelding en sla de afmetingen op
  useEffect(() => {
    if (!src) {
      setLoading(true);
      return;
    }

    setLoading(true);
    const img = new window.Image();
    img.src = src;
    img.onload = () => {
      setImgSize({ width: img.width, height: img.height });
      setLoading(false);
    };
    img.onerror = () => {
      setLoading(false);
    };
  }, [src]);

  // 2. Bereken initialScale bij imgSize of container resize
  useEffect(() => {
    if (!containerRef.current || !imgSize.width || imgSize.width <= 10) {
      return;
    }

    const updateScale = () => {
      // De ResizeObserver kan nog één keer vuren nadat de component is ontkoppeld (bv. bij het
      // sluiten of doorbladeren van de editor-popup in de booklayout-view); de ref is dan null.
      const container = containerRef.current;
      if (!container) return;
      const { offsetWidth, offsetHeight } = container;
      const scaleX = offsetWidth / imgSize.width;
      const scaleY = offsetHeight / imgSize.height;

      setInitialScale(Math.min(scaleX, scaleY));
    };

    updateScale();

    // Herbereken bij resize van container
    const resizeObserver = new window.ResizeObserver(updateScale);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [imgSize]);

  // 3. Reset transform na initialScale update
  useEffect(() => {
    if (transformWrapperRef.current) {
      transformWrapperRef.current.resetTransform(0);
      setTimeout(() => {
        if (transformWrapperRef.current) transformWrapperRef.current.centerView(undefined, 0);
      }, 201);
    }
  }, [initialScale]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: '#f8f8f8',
        border: '1px solid #eee',
        borderRadius: '4px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {loading || !src ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <div
            className="spinner"
            style={{
              border: '4px solid rgba(0, 0, 0, 0.1)',
              borderLeftColor: '#35b3ee',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              animation: 'spin 1s linear infinite',
            }}
          />
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      ) : (
        initialScale &&
        initialScale < 10 &&
        imgSize &&
        imgSize.width > 10 && (
          <TransformWrapper
            ref={transformWrapperRef}
            centerOnInit={true}
            initialScale={initialScale}
            minScale={initialScale}
            limitToBounds={false}
            alignmentAnimation={{ disabled: true }}
          >
            {({ zoomIn, zoomOut, resetTransform, centerView }) => (
              <>
                <TransformComponent>
                  <div
                    title={`${t('hintMouseZoom')}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={src}
                      // style={{
                      //   maxWidth: "100%",
                      //   maxHeight: "100%",
                      //   display: "block",
                      //   margin: "0 auto"
                      // }}
                      alt=""
                    />
                  </div>
                </TransformComponent>
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    bottom: '20px',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '10px',
                    background: 'rgba(255, 255, 255, 0.8)',
                    padding: '8px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    zIndex: 1000,
                    marginTop: '10px',
                  }}
                >
                  <button onClick={() => zoomOut()}>-</button>
                  <button
                    onClick={() => {
                      resetTransform();
                      centerView(initialScale);
                    }}
                    title={`${t('hintReset')}`}
                  >
                    Reset
                  </button>
                  <button onClick={() => zoomIn()}>+</button>
                </div>
              </>
            )}
          </TransformWrapper>
        )
      )}
    </div>
  );
};

export default PageCardEditor;
